import moment from "moment-timezone";
import {
  buildTimeslotDateRange,
  extractSchedulingUrl,
  getRegistrationTimezone,
  isFlatRdTimeslotList,
  mapRdSlotResponseToAppointmentWeek,
} from "./registrationAppointmentTimeslots";
import { resolvePreselectedSchedulingUrl } from "./calendlyBooking";
import { mapMatchedRdToSelectionCard, mapMatchedRdToProfile } from "../Dietitian/registrationMatchedRd";
import { getRegistrationData } from "../registrationSetupStorage";
import {
  redirectIfNoProviderFromListRdTimeslotResponse,
  redirectIfProviderUnavailableBeforeApi,
} from "../registrationRdAvailability";

const buildSlotKey = (date, startTime) => `${date}|${startTime}`;

export const buildListRdTimeslotPayload = (
  timezone = getRegistrationTimezone(),
  weekOffset = 0
) => {
  const dates = buildTimeslotDateRange(timezone, weekOffset);
  return {
    start_date: dates.start_date,
    end_date: dates.end_date,
    timezone: dates.timezone,
  };
};

/** Flatten nested rd_slot day blocks and dedupe by date + start_time. */
export const dedupeAggregateRdSlots = (rdSlot) => {
  const slotList = Array.isArray(rdSlot) ? rdSlot : [];
  const deduped = new Map();
  const slotsByKey = {};

  const addSlot = (slot, dayDate) => {
    const startTime = slot?.start_time ?? slot?.startTime;
    const date =
      slot?.date ?? dayDate ?? (startTime ? startTime.slice(0, 10) : null);

    if (!startTime || !date) {
      return;
    }

    const key = buildSlotKey(date, startTime);
    if (!deduped.has(key)) {
      deduped.set(key, {
        ...slot,
        date,
        start_time: startTime,
      });
    } else {
      const existing = deduped.get(key);
      const mergedRdInfo = [
        ...(Array.isArray(existing.rd_info) ? existing.rd_info : []),
        ...(Array.isArray(slot.rd_info) ? slot.rd_info : []),
      ];
      const uniqueRdInfo = mergedRdInfo.filter(
        (rd, index, list) =>
          list.findIndex((item) => String(item?.id) === String(rd?.id)) === index
      );
      deduped.set(key, {
        ...existing,
        rd_info: uniqueRdInfo,
      });
    }
    slotsByKey[key] = deduped.get(key);
  };

  if (isFlatRdTimeslotList(slotList)) {
    slotList.forEach((slot) => addSlot(slot));
  } else {
    slotList.forEach((dayBlock) => {
      const dayDate = dayBlock?.date ?? dayBlock?.slot_date;
      const nestedSlots = Array.isArray(dayBlock?.slots) ? dayBlock.slots : [];

      nestedSlots.forEach((slot) => addSlot(slot, dayDate));
    });
  }

  const flatSlots = Array.from(deduped.values());
  const dayMap = new Map();

  flatSlots.forEach((slot) => {
    const date = slot.date;
    if (!dayMap.has(date)) {
      dayMap.set(date, { date, slots: [] });
    }
    dayMap.get(date).slots.push(slot);
  });

  return {
    rdSlot: Array.from(dayMap.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    ),
    slotsByKey,
  };
};

export const fetchRegistrationNoPreferenceAppointmentWeek = async ({
  language,
  apiService,
  signal,
  weekOffset = 0,
  history = null,
}) => {
  if (history && redirectIfProviderUnavailableBeforeApi(history, getRegistrationData())) {
    return { redirected: true };
  }

  const timezone = getRegistrationTimezone();
  const payload = buildListRdTimeslotPayload(timezone, weekOffset);
  const res = await apiService.listRegistrationRdTimeslot(payload, language, {
    signal,
  });

  if (history && redirectIfNoProviderFromListRdTimeslotResponse(history, res)) {
    return { redirected: true };
  }

  if (!res?.status) {
    throw new Error(res?.message || "NUTRITION_TIMESLOT_ERR_LOAD");
  }

  const rawRdSlot = Array.isArray(res?.data?.rd_slot)
    ? res.data.rd_slot
    : Array.isArray(res?.data)
      ? res.data
      : [];

  const { rdSlot, slotsByKey } = dedupeAggregateRdSlots(rawRdSlot);
  const appointmentWeek = mapRdSlotResponseToAppointmentWeek(
    rdSlot,
    payload.start_date,
    payload.end_date,
    payload.timezone
  );

  return {
    payload,
    rdSlot,
    slotsByKey,
    appointmentWeek,
    isDefaultRd: Boolean(res?.data?.is_default_rd),
  };
};

export const normalizeRdInfoForCard = (rdInfo) => {
  if (!rdInfo || typeof rdInfo !== "object") {
    return null;
  }

  return {
    id: rdInfo.id,
    first_name: rdInfo.first_name,
    last_name: rdInfo.last_name,
    profile_image: rdInfo.profile_image,
    email: rdInfo.email,
    rd_detail: rdInfo.rd_detail,
    categories: Array.isArray(rdInfo.rd_categories)
      ? rdInfo.rd_categories.map((item) => item?.category).filter(Boolean)
      : [],
    rd_calendly_urls: rdInfo.calendly_url ? [rdInfo.calendly_url] : [],
    profile_text: rdInfo.rd_detail?.profile_text,
    scheduling_url: rdInfo.scheduling_url,
  };
};

export const mapRdInfoListToSelectionCards = (rdInfoList, slotStartTime) => {
  if (!Array.isArray(rdInfoList)) {
    return [];
  }

  return rdInfoList
    .map((rdInfo, index) => {
      const normalized = normalizeRdInfoForCard(rdInfo);
      if (!normalized) {
        return null;
      }
      const schedulingUrl = resolvePreselectedSchedulingUrl(
        rdInfo,
        slotStartTime
      );
      return {
        ...mapMatchedRdToSelectionCard(normalized, index),
        creds: "",
        schedulingUrl,
        raw: rdInfo,
      };
    })
    .filter(Boolean);
};

export const mapRdInfoToProfile = (rdInfo) => {
  const normalized = normalizeRdInfoForCard(rdInfo);
  if (!normalized) {
    return null;
  }
  return mapMatchedRdToProfile(normalized);
};

export const buildNoPreferenceAppointmentSlot = (cell, rowTime) => {
  const schedulingUrl =
    cell.schedulingUrl || extractSchedulingUrl(cell.slot);
  return {
    day: cell.dayKey,
    time: rowTime,
    dateKey: cell.dateKey,
    startTime: cell.slot?.start_time ?? cell.slot?.startTime,
    schedulingUrl,
    location: cell.slot?.location,
    type: cell.slot?.type,
    raw: cell.slot ?? null,
    slotKey: buildSlotKey(
      cell.dateKey,
      cell.slot?.start_time ?? cell.slot?.startTime ?? ""
    ),
  };
};

export const getNoPreferenceSlotData = (slotsByKey, selectedSlot) => {
  if (!selectedSlot || !slotsByKey) {
    return null;
  }
  if (selectedSlot.slotKey && slotsByKey[selectedSlot.slotKey]) {
    return slotsByKey[selectedSlot.slotKey];
  }
  const key = buildSlotKey(selectedSlot.dateKey, selectedSlot.startTime);
  return slotsByKey[key] ?? null;
};

export const getEarliestNoPreferenceSlotLabel = (
  appointmentWeek,
  timezone = getRegistrationTimezone()
) => {
  if (!appointmentWeek || !Array.isArray(appointmentWeek.periods)) {
    return "";
  }

  let earliest = null;
  appointmentWeek.periods.forEach((period) => {
    (period.rows || []).forEach((row) => {
      (row.cells || []).forEach((cell) => {
        if (cell?.status !== "available") {
          return;
        }
        const dateKey = cell.dateKey;
        const timeLabel = cell.time;
        if (!dateKey || !timeLabel) {
          return;
        }
        const candidate = moment.tz(
          `${dateKey} ${timeLabel}`,
          "YYYY-MM-DD h:mm A",
          timezone
        );
        if (!candidate.isValid()) {
          return;
        }
        if (!earliest || candidate.isBefore(earliest)) {
          earliest = candidate;
        }
      });
    });
  });

  return earliest ? earliest.format("ddd MMM D · h:mm A") : "";
};

export const persistNoPreferenceSlotSelection = ({
  selectedSlot,
  slotData,
  updateRegistrationData,
}) => {
  const rdList = Array.isArray(slotData?.rd_info) ? slotData.rd_info : [];
  updateRegistrationData({
    noPreferenceSelectedSlot: selectedSlot,
    noPreferenceSlotData: slotData,
    noPreferenceRdList: rdList,
  });
  return rdList;
};

export const persistNoPreferenceRdSelection = ({
  rdCard,
  selectedSlot,
  updateRegistrationData,
}) => {
  updateRegistrationData({
    noPreferenceSelectedRd: {
      rdId: String(rdCard.id),
      raw: rdCard.raw,
      view: rdCard,
    },
    selectedDietitian: {
      id: rdCard.id,
      name: rdCard.name,
      noPreference: true,
      raw: rdCard.raw,
    },
    noPreferenceSelectedSlot: selectedSlot,
  });
};

export const buildNoPreferenceSelectedAppointment = ({
  selectedSlot,
  rdCard,
}) => {
  const rdInfo = rdCard?.raw ?? rdCard;
  const schedulingUrl = resolvePreselectedSchedulingUrl(
    rdInfo,
    selectedSlot?.startTime
  );

  return {
    ...selectedSlot,
    schedulingUrl,
    rdId: String(rdCard.id),
  };
};
